import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {CpConfigChildTab} from '../../../../../interface/cp-config-child-tab.interface';
import {
    CpProvisionTypeComponentOption,
    CpProvisionTypeLookupService
} from '../../../../../service/ql/cp-provision-type-lookup.service';

/**
 * navItem "Berechnete Vermittler" of the ProvisionType object component — the configuration
 * surface of K-17 `cp.ProvisionTypeCalculatedAgent` (requirement §4).
 *
 * The table itself is the generic {@link CpProvisionTypeChildTableComponent} on
 * `cp.ProvisionTypeCalculatedAgent`: same descriptor mechanism, same FK pre-fill, same fixed column
 * block (Name, Alias, Bedingungssatz, Priorität, …) and the same read-only condition preview as the
 * five sibling assignment tables, because K-17 references a condition set exactly like they do and
 * is matched by the identical `ProvisionAssignmentMatchingService`. This component adds exactly
 * ONE thing on top, and for exactly the reason the sibling
 * {@link CpProvisionTypeMatrixAssignmentComponent} adds it: the pre-filter on the commission
 * component.
 *
 * ## Why the pre-filter belongs here too
 * K-10 got the pre-filter because it is partitioned by a MANDATORY `provisionTypeComponent` FK —
 * the rule set of a provision type with three components is three interleaved rule sets in one
 * table, and the priority order of a single component is unreadable out of that mixture. K-17 has
 * the same shape through `targetProvisionTypeComponent` (NOT NULL, requirement §3.1 group A): the
 * derived `ProvisionContractAgent` row hangs off exactly one component, so the rule set is
 * partitioned the same way and reads the same way. The filter is an operating aid on the READ side
 * only — it narrows the list and changes nothing about how rows are stored or created; the create
 * form still pre-fills the provision type and lets the user pick the target component.
 *
 * "Alle Zielkomponenten" (`null`) is the default, so the tab opens on the complete rule set.
 *
 * The filter is worded "Zielkomponente", never "Provisionsart": inside this object page the
 * latter is the label of the PARENT entity `cp.ProvisionType`
 * (`@UiEntityInfo(label = "Provisionsart")`, ProvisionType.java:62), while the entity actually
 * filtered here is `cp.ProvisionTypeComponent` — backend label "Provisionsart-Komponente".
 *
 * ## Widget identity across filter changes (CONFIG-011)
 * Switching the pre-filter must not look like a different table: the child component keeps its
 * alias `config.cp.ProvisionTypeCalculatedAgent.table` for every filter value, so column widths and
 * sorting survive; the column set is fixed, so it does not shift while the user pages through
 * components.
 *
 * ## No local role label map
 * `triggerAgentRole` renders through the generic table; its German labels come from the backend
 * `@UiEnumValueInfo` metadata via the FormObject/QL pipeline. This component deliberately carries
 * no hardcoded label map for it — see `enum/provision-agent-role.enum.ts`.
 */
@Component({
    selector: 'cp-provision-type-calculated-agent',
    templateUrl: './cp-provision-type-calculated-agent.component.html',
    styleUrls: ['./cp-provision-type-calculated-agent.component.scss'],
    standalone: false
})
export class CpProvisionTypeCalculatedAgentComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Descriptor of the calculated-agent child list, from `CpProvisionTypeBaseComponent`. */
    @Input() child: CpConfigChildTab;

    /** "Im Bedingungssatz öffnen" of the condition preview, forwarded to the object view. */
    @Output() onJumpToConditionSet = new EventEmitter<number>();

    /** Commission components of this provision type — the options of the pre-filter. */
    componentOptions: CpProvisionTypeComponentOption[] = [];

    /** Selected target component; `null` = no pre-filter, the complete rule set is shown. */
    selectedComponentId: number | null = null;

    /** Handed to the child table; `null` while nothing is pre-filtered. */
    additionalFilters: FilterCriteria[] = null;

    private componentSubscription: Subscription;

    constructor(private lookupService: CpProvisionTypeLookupService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.cancelComponentLoad();

        this.selectedComponentId = null;
        this.additionalFilters = null;
        this.componentOptions = [];

        if (!this.provisionTypeId) {
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.componentSubscription = this.lookupService
            .getComponents(this.provisionTypeId)
            .subscribe(options => {
                this.componentOptions = options ?? [];
                this.busy = false;
                this.initialized = true;
            });

        this.initialized = true;
    }

    /**
     * Apply the pre-filter. `null` clears it and returns the complete rule set of the provision
     * type — the state the tab opens in.
     */
    onComponentChange(componentId: number | null): void {
        this.selectedComponentId = componentId ?? null;
        this.additionalFilters = this.selectedComponentId
            ? [FilterCriteria.create(
                'e.targetProvisionTypeComponentId', FilterCriteria.cOperatorEqual, this.selectedComponentId)]
            : null;
    }

    private cancelComponentLoad(): void {
        if (this.componentSubscription) {
            this.componentSubscription.unsubscribe();
            this.componentSubscription = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId'] || changes['child']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.cancelComponentLoad();
    }

}
